// Earnings (settlement) workspace — /analytics/earnings. Answers a facility
// OWNER's "what am I owed?": gross collected → platform-fee deduction → refunds
// → NET OWED, by payout period, with the next payout date.
//
// THIN presentation layer: every figure is a real backend field rendered via the
// shared money formatter. There is NO client-side math — the NET-OWED headline
// is the endpoint's `total_net_owed` VERBATIM (a client re-sum of the period
// rows could diverge and lie). FACILITY framing: `net_owed` is what the facility
// is PAID (the hero); `platform_fees` is the platform's DEDUCTION (never the
// facility's "earnings" headline). Contrast the platform finance view where
// platform_fees IS the platform's revenue.
//
// Honest states reuse the platform FinancePage patterns (a discriminated
// summary state so an error "Unavailable" is never confused with an empty/mixed
// "—", and a mixed-currency aggregate is never flattened into a fabricated
// "$0.00"):
//   loading -> a role=status Spinner (not "—" presented as truth)
//   error   -> a role=alert + Retry (refetch); headline reads "Unavailable"
//   empty   -> "No settlements yet." (no fabricated $0.00)
//   mixed   -> currency=null with periods: aggregate totals "—" + a note; the
//              per-period rows keep their OWN currency
//   ok      -> real, single-currency figures
//
// Conventions mirror revenue-analytics-workspace.tsx: the useResolvedFacility
// gate (#883 — no facilityId → ZERO reads; the Layer-3 FacilityGate owns the
// real "no facility" screen), plain useEffect + useState, native MUI
// presentation, shared money/date helpers.

import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { formatDate, formatMoney } from "@bw/shared/utils";
import { useEffect, useState, type ElementType } from "react";
import {
  getFacilitySettlements,
  type SettlementPeriodResponse,
  type SettlementSummaryResponse,
} from "@/libs/analyticsApi";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { financeApi } from "@bw/shared/api";
import { useMyOrg } from "@/hooks/use-my-org";
import { useFinanceContext } from "@/hooks/use-finance-context";
import { PayoutLedger } from "@/components/analytics/payout-ledger";
import { ReconciliationSection } from "@/components/analytics/reconciliation-ledger";

const PLACEHOLDER = "—";

// Discriminated headline state. Keeping these distinct stops an error "—" from
// reading as a genuine empty/mixed "—", and stops a mixed-currency aggregate
// from being shown as a fabricated "$0.00". Every figure traces to the endpoint.
type SummaryState = "loading" | "error" | "empty" | "mixed" | "ok";

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon: ElementType;
}) {
  return (
    <Paper sx={{ p: 1.5 }} variant="outlined">
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Box
          sx={{
            bgcolor: "action.selected",
            borderRadius: 2,
            color: "primary.main",
            display: "grid",
            flexShrink: 0,
            height: 40,
            placeItems: "center",
            width: 40,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}
            variant="h6"
          >
            {value}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {label}
          </Typography>
          {hint ? (
            <Typography
              color="text.secondary"
              sx={{ display: "block" }}
              variant="caption"
            >
              {hint}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

export function SettlementsWorkspace() {
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  // Payouts are ORG-scoped (the facility object carries no org_id), so resolve
  // the org id from the org-status seam and pass it to the org-scoped
  // PayoutLedger below.
  const org = useMyOrg().org;
  const orgId = org?.org_id ?? null;
  // Payouts and reconciliation are org finance data (server: org:finance_read);
  // facility settlements above only need facility:read_internal.
  const finance = useFinanceContext(orgId, financeApi, org?.finance_capability === "allowed");
  const financeRead = finance.context?.allowed_actions.includes("org:finance_read") === true;

  const [summary, setSummary] = useState<SettlementSummaryResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  // A failed fetch must NEVER fabricate $0.00 — gate the headline into
  // "Unavailable" via this flag (distinct from the honest empty/mixed "—").
  const [error, setError] = useState(false);
  // Bumped by Retry to force a refetch of the same window.
  const [reload, setReload] = useState(0);

  useEffect(() => {
    // Gate the read on a truthy, org-verified facilityId (#883). No facility
    // resolved yet → ZERO api calls.
    if (!facilityId) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    void (async () => {
      try {
        const data = await getFacilitySettlements(facilityId);
        if (cancelled) return;
        setSummary(data);
      } catch {
        if (cancelled) return;
        // Never echo the raw backend message; an honest, generic error state.
        setSummary(null);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, reload]);

  // Brief resolve window before the FacilityContext selection lands. The
  // Layer-3 gate owns the genuine "no facility" case, so we just show loading
  // and fire NO read.
  if (facilityLoading || !facilityId) {
    return (
      <Stack
        aria-label="Loading earnings"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  const periods = summary?.periods ?? [];
  const hasSettlements = periods.length > 0;
  // Aggregate totals are only meaningful in a single currency. The backend
  // signals mixed currencies with `currency: null`, expecting the reader to
  // fall back to the per-period rows.
  const aggregateCurrency = summary?.currency ?? null;
  const isMixedCurrency = hasSettlements && aggregateCurrency === null;

  const summaryState: SummaryState = error
    ? "error"
    : loading
      ? "loading"
      : !hasSettlements
        ? "empty"
        : isMixedCurrency
          ? "mixed"
          : "ok";

  // A single-currency money total, or an honest non-figure for every other
  // state. Error → "Unavailable" (NOT "—", so it is never read as a real
  // "earned nothing"); loading/empty/mixed → "—". Never a fabricated $0.00.
  const money = (
    value:
      | SettlementSummaryResponse[keyof SettlementSummaryResponse]
      | undefined,
  ) =>
    summaryState === "error"
      ? "Unavailable"
      : summaryState === "ok" && summary
        ? formatMoney(value as string, aggregateCurrency ?? "USD")
        : PLACEHOLDER;

  const netOwedValue = money(summary?.total_net_owed);
  const grossValue = money(summary?.total_gross);
  const platformFeesValue = money(summary?.total_platform_fees);
  const nextPayoutValue =
    summaryState === "error"
      ? "Unavailable"
      : summary?.next_payout_date
        ? formatDate(summary.next_payout_date)
        : PLACEHOLDER;

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 2 }}>
      {/* The app shell supplies the "Earnings" page title, so the content does
          NOT repeat it (ui-quality.md). The in-content heading is the facility
          name. */}
      <Box>
        <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
          {facility?.name ?? "Your facility"}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Settlements and earnings for your facility
        </Typography>
      </Box>

      {/* Error owns a single alert + Retry; the headline figures read
          "Unavailable" (never a fabricated $0). */}
      {summaryState === "error" ? (
        <Alert
          action={
            <Button
              color="inherit"
              onClick={() => setReload((n) => n + 1)}
              size="small"
              type="button"
              variant="outlined"
            >
              Retry
            </Button>
          }
          severity="error"
        >
          We couldn&apos;t load settlements. Figures are unavailable.
        </Alert>
      ) : null}

      {/* Mixed-currency note — the aggregate totals are "—" (not aggregable);
          the per-period rows below keep their own currency. */}
      {summaryState === "mixed" ? (
        <Alert severity="info">
          Multiple currencies — see per-period rows below.
        </Alert>
      ) : null}

      {/* KPI strip — Net owed is the OWNER headline (what the facility is paid).
          Gross collected and Platform fees (a deduction) sit beside it; Next
          payout is the scheduled date. Every value from a backend field. */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          },
        }}
      >
        {/* The OWNER hero: NET owed to this facility (what you take home after
            the platform-fee deduction + refunds). Labelled "Net to you" — kept
            clear of the words "net owed"/"payout" so the only element carrying
            "payout" is the dedicated "Next payout" tile (one label, one
            concept). The figure is the endpoint's total_net_owed VERBATIM. */}
        <Kpi
          hint="After fees"
          icon={AccountBalanceOutlinedIcon}
          label="Net to you"
          value={summaryState === "loading" ? PLACEHOLDER : netOwedValue}
        />
        <Kpi
          icon={AttachMoneyIcon}
          label="Gross collected"
          value={summaryState === "loading" ? PLACEHOLDER : grossValue}
        />
        <Kpi
          hint="Deducted"
          icon={ReceiptLongOutlinedIcon}
          label="Platform fees"
          value={summaryState === "loading" ? PLACEHOLDER : platformFeesValue}
        />
        <Kpi
          icon={CalendarMonthOutlinedIcon}
          label="Next payout"
          value={summaryState === "loading" ? PLACEHOLDER : nextPayoutValue}
        />
      </Box>

      {/* Per-period ledger — its own honest loading / empty states. On error the
          alert above owns the single signal, so this section stays out of the
          way. Each row formats money in its OWN period.currency, so a
          mixed-currency response still renders honest per-row figures. */}
      {summaryState !== "error" ? (
        <SettlementLedger
          loading={summaryState === "loading"}
          periods={periods}
          aggregateCurrency={aggregateCurrency}
        />
      ) : null}

      {/* Payout history (org-scoped) — the disbursement record beside the
          accrual-based settlement ledger above. Gated on a resolved orgId so it
          renders nothing (and fires no read) until the org is known. */}
      {orgId && financeRead ? <PayoutLedger orgId={orgId} /> : null}

      {/* Reconciliation (org-scoped) — does the accrual net match the money that
          actually moved? Sits after the payout ledger, gated on the same resolved
          orgId so it renders nothing (and fires no read) until the org is known. */}
      {orgId && financeRead ? <ReconciliationSection orgId={orgId} /> : null}
    </Stack>
  );
}

/**
 * Per-period settlement table with section-local honest states. A loading fetch
 * shows a role=status Spinner; an empty ledger shows "No settlements yet." (no
 * fabricated $0.00). The error state is owned by the headline region above, so
 * this section only renders on non-error states. Each row formats money in its
 * OWN `period.currency`.
 */
function SettlementLedger({
  loading,
  periods,
  aggregateCurrency,
}: {
  loading: boolean;
  periods: SettlementPeriodResponse[];
  aggregateCurrency: string | null;
}) {
  return (
    <Paper component="section" sx={{ overflow: "hidden" }} variant="outlined">
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <Typography component="h2" variant="h6">
          Settlement periods
        </Typography>
      </Box>
      <Divider />
      <Box>
        {loading ? (
          <Stack
            aria-label="Loading settlement periods"
            role="status"
            spacing={1}
            sx={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: 128,
            }}
          >
            <CircularProgress aria-hidden size={24} />
            <Typography color="text.secondary" variant="body2">
              Loading…
            </Typography>
          </Stack>
        ) : periods.length === 0 ? (
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: 128,
            }}
          >
            <Typography color="text.secondary" variant="body2">
              No settlements yet.
            </Typography>
          </Stack>
        ) : (
          <TableContainer>
            <Table aria-label="Settlement periods" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Platform fees</TableCell>
                  <TableCell align="right">Refunds</TableCell>
                  {/* "Net" (not "Net owed") keeps the only "net owed"-bearing
                      node the headline KPI. The date column is the NEUTRAL
                      "Payout" (matching the platform FinancePage reference), NOT
                      the past-tense "Paid out": for the accruing window
                      payout_date == next_payout_date is a FUTURE date, and "Paid
                      out: <future>" would lie that money was already disbursed.
                      In this gross→fees→refunds→net row the columns are
                      unambiguous. */}
                  <TableCell align="right">Net</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payout</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periods.map((period) => {
                  // Each row uses its OWN currency, so a mixed-currency summary
                  // still renders honest per-row figures even when the aggregate
                  // is not aggregable.
                  const currency =
                    period.currency ?? aggregateCurrency ?? "USD";
                  return (
                    <TableRow
                      key={`${period.period_start}-${period.period_end}`}
                    >
                      <TableCell
                        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {formatDate(period.period_start)} –{" "}
                        {formatDate(period.period_end)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatMoney(period.gross, currency)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatMoney(period.platform_fees, currency)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatMoney(period.refunds, currency)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatMoney(period.net_owed, currency)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          color={
                            period.status === "closed" ? "success" : "default"
                          }
                          label={period.status}
                          size="small"
                          variant={
                            period.status === "closed" ? "filled" : "outlined"
                          }
                        />
                      </TableCell>
                      <TableCell
                        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {formatDate(period.payout_date)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
}
